// features/findings/refugia-template.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — The Refugia Template (v1)
// Form 4 — Pattern across cases. Pre-registered 2026-05-08.
//
// The fifth popdec finding. Cross-case validation of the seven-condition
// framework introduced in Israel-Haredi Floor (Form 1). Six documented
// cases + one secular failure analog (kibbutz). Tests whether the
// framework generalizes; tests directionality (lift when conditions
// hold, decay when they weaken).
//
// Reading order: korea-hungary-divergence → korea-lockin →
// positive-case-search → israel-haredi-floor → this. Together they
// form the complete five-finding popdec arc.
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
} from './components-shared';
import {
  CoOccurrenceMatrix,
  CountryScatter,
  DivergenceTimeline,
  PredictionsBlock,
  MagnitudeBars,
} from './charts';

// ─── Chart data ─────────────────────────────────────────────────────

// Co-occurrence matrix — cases (columns) × conditions (rows).
// State: 'present' (sage), 'partial' (brass), 'absent' (oxblood).
// Outcome: 'sustained', 'transitional', 'partial', 'failed', 'sub-replacement'.
const REFUGIA_CASES = [
  { id: 'haredi',        shortLabel: 'Israel-Haredi',     outcome: 'sustained' },
  { id: 'hutterite',     shortLabel: 'Hutterite',         outcome: 'sustained' },
  { id: 'amish',         shortLabel: 'Amish',             outcome: 'sustained' },
  { id: 'mormon-utah',   shortLabel: 'Mormon Utah',       outcome: 'sustained' },
  { id: 'arab-muslim',   shortLabel: 'Israel-Arab Muslim', outcome: 'transitional' },
  { id: 'roma',          shortLabel: 'Roma settlements',  outcome: 'partial' },
  { id: 'kibbutz',       shortLabel: 'Kibbutz (1985-95)', outcome: 'failed' },
  { id: 'kr-rural-xn',   shortLabel: 'Korean rural Xn (P5 projection)', outcome: 'partial' },
  { id: 'israel-sec',    shortLabel: 'Israel-secular',    outcome: 'sub-replacement' },
];

const REFUGIA_CONDITIONS = [
  {
    id: 'c1',
    label: 'C1 — Religious-community density (peer-parents-visibility ~100%)',
    values: {
      'haredi':     { state: 'present' },
      'hutterite':  { state: 'present' },
      'amish':      { state: 'present' },
      'mormon-utah':{ state: 'present' },
      'arab-muslim':{ state: 'partial' },
      'roma':       { state: 'present' },
      'kibbutz':    { state: 'absent' },
      'kr-rural-xn':{ state: 'partial' },
      'israel-sec': { state: 'absent' },
    },
  },
  {
    id: 'c2',
    label: 'C2 — Multi-generational household economics',
    values: {
      'haredi':     { state: 'present' },
      'hutterite':  { state: 'present' },
      'amish':      { state: 'present' },
      'mormon-utah':{ state: 'partial' },
      'arab-muslim':{ state: 'partial' },
      'roma':       { state: 'present' },
      'kibbutz':    { state: 'absent' },
      'kr-rural-xn':{ state: 'partial' },
      'israel-sec': { state: 'partial' },
    },
  },
  {
    id: 'c3',
    label: 'C3 — Alternative-status structure (decoupled from income)',
    values: {
      'haredi':     { state: 'present' },
      'hutterite':  { state: 'present' },
      'amish':      { state: 'present' },
      'mormon-utah':{ state: 'partial' },
      'arab-muslim':{ state: 'absent' },
      'roma':       { state: 'absent' },
      'kibbutz':    { state: 'absent' },
      'kr-rural-xn':{ state: 'absent' },
      'israel-sec': { state: 'absent' },
    },
  },
  {
    id: 'c4',
    label: 'C4 — Kinship-network childcare',
    values: {
      'haredi':     { state: 'present' },
      'hutterite':  { state: 'present' },
      'amish':      { state: 'present' },
      'mormon-utah':{ state: 'partial' },
      'arab-muslim':{ state: 'partial' },
      'roma':       { state: 'present' },
      'kibbutz':    { state: 'absent' },
      'kr-rural-xn':{ state: 'partial' },
      'israel-sec': { state: 'partial' },
    },
  },
  {
    id: 'c5',
    label: 'C5 — Identity-narrative coherence around family',
    values: {
      'haredi':     { state: 'present' },
      'hutterite':  { state: 'present' },
      'amish':      { state: 'present' },
      'mormon-utah':{ state: 'present' },
      'arab-muslim':{ state: 'partial' },
      'roma':       { state: 'partial' },
      'kibbutz':    { state: 'absent' },
      'kr-rural-xn':{ state: 'partial' },
      'israel-sec': { state: 'partial' },
    },
  },
  {
    id: 'c6',
    label: 'C6 — Marriage-formation infrastructure',
    values: {
      'haredi':     { state: 'present' },
      'hutterite':  { state: 'present' },
      'amish':      { state: 'present' },
      'mormon-utah':{ state: 'present' },
      'arab-muslim':{ state: 'absent' },
      'roma':       { state: 'absent' },
      'kibbutz':    { state: 'absent' },
      'kr-rural-xn':{ state: 'absent' },
      'israel-sec': { state: 'partial' },
    },
  },
  {
    id: 'c7',
    label: 'C7 — Exit cost (structural stability against drift)',
    values: {
      'haredi':     { state: 'present' },
      'hutterite':  { state: 'present' },
      'amish':      { state: 'present' },
      'mormon-utah':{ state: 'partial' },
      'arab-muslim':{ state: 'absent' },
      'roma':       { state: 'partial' },
      'kibbutz':    { state: 'absent' },
      'kr-rural-xn':{ state: 'absent' },
      'israel-sec': { state: 'absent' },
    },
  },
];

// Cross-case scatter — condition count (x) × cohort TFR (y).
// Counts are estimated whole-number (partial counted as 0.5 each).
// TFR are 1985-95 cohort completed or projected. Sources: CBS Israel,
// US Census, Eaton & Mayer 1953, DellaPergola, Wittgenstein Centre,
// Human Fertility Database.
const REFUGIA_SCATTER = [
  { label: 'Israel-Haredi',        x: 7.0, y: 6.5, tone: 'sage',     labelDx: 8 },
  { label: 'Amish',                x: 7.0, y: 6.2, tone: 'sage',     labelDx: 8, labelDy: 16 },
  { label: 'Hutterite',            x: 7.0, y: 6.0, tone: 'sage',     labelDx: 8, labelDy: 32 },
  { label: 'Mormon Utah',          x: 5.5, y: 2.7, tone: 'sage',     labelDx: 8 },
  { label: 'Roma settlements',     x: 4.0, y: 3.2, tone: 'brass',    labelDx: 8 },
  { label: 'Israel-Arab Muslim',   x: 3.0, y: 3.0, tone: 'brass',    labelDx: 8, labelDy: -8 },
  { label: 'Israel-secular',       x: 2.5, y: 2.1, tone: 'brass',    labelDx: 8 },
  { label: 'Kibbutz 1985-95',      x: 2.0, y: 2.1, tone: 'oxblood',  labelDx: 8, labelDy: 16, outlier: true },
  { label: 'Korean rural Xn (P5 projection)', x: 2.5, y: 1.4, tone: 'plum',    labelDx: 8, labelDy: -8 },
  { label: 'France',               x: 1.0, y: 1.62, tone: 'plum',    labelDx: 8 },
  { label: 'Sweden',               x: 0.5, y: 1.65, tone: 'plum',    labelDx: 8, labelDy: 16 },
  { label: 'Hungary urban',        x: 0.5, y: 1.50, tone: 'plum',    labelDx: 8, labelDy: -8 },
  { label: 'Korea Seoul',          x: 0.0, y: 0.55, tone: 'oxblood', labelDx: 8 },
];

// Trajectory series — declining cases that show condition-loss → fertility-loss.
// Birth-cohort midpoint × cohort fertility. The kibbutz line shows the lift
// while conditions held and the collapse as conditions decayed. Israel-Arab
// Muslim shows the longer transitional decline.
const TRAJECTORY_SERIES = [
  {
    id: 'kibbutz',
    label: 'Kibbutz cohort',
    color: 'oxblood',
    points: [
      { x: 1935, y: 3.6 },
      { x: 1945, y: 3.9 },
      { x: 1955, y: 3.2 },
      { x: 1965, y: 2.8 },
      { x: 1975, y: 2.4 },
      { x: 1985, y: 2.1 },
      { x: 1990, y: 2.1 },
    ],
  },
  {
    id: 'israel-arab',
    label: 'Israel-Arab Muslim',
    color: 'cognac',
    points: [
      { x: 1955, y: 6.5 },
      { x: 1965, y: 5.8 },
      { x: 1975, y: 4.5 },
      { x: 1985, y: 3.5 },
      { x: 1990, y: 3.0 },
    ],
  },
  {
    id: 'mormon-utah',
    label: 'Mormon Utah',
    color: 'forest-moss',
    points: [
      { x: 1955, y: 4.0 },
      { x: 1965, y: 3.8 },
      { x: 1975, y: 3.2 },
      { x: 1985, y: 2.9 },
      { x: 1990, y: 2.7 },
    ],
  },
  {
    id: 'haredi-flat',
    label: 'Israel-Haredi (anchor)',
    color: 'sage',
    points: [
      { x: 1955, y: 6.4 },
      { x: 1965, y: 6.6 },
      { x: 1975, y: 6.6 },
      { x: 1985, y: 6.5 },
      { x: 1990, y: 6.5 },
    ],
  },
];

// Per-case condition count (sum of present=1 + partial=0.5 + absent=0)
// next to cohort TFR. Magnitude bar visualization of the count distribution.
const CONDITION_COUNT_BARS = [
  { label: 'Israel-Haredi (7.0/7) — TFR 6.5',           value: 7.0, tone: 'sage' },
  { label: 'Hutterite (7.0/7) — TFR 6.0',                value: 7.0, tone: 'sage' },
  { label: 'Amish (7.0/7) — TFR 6.2',                    value: 7.0, tone: 'sage' },
  { label: 'Mormon Utah (5.5/7) — TFR 2.7',              value: 5.5, tone: 'forest-moss' },
  { label: 'Roma settlements (4.0/7) — TFR 3.2',         value: 4.0, tone: 'brass' },
  { label: 'Israel-Arab Muslim (3.0/7) — TFR 3.0',      value: 3.0, tone: 'brass' },
  { label: 'Israel-secular (2.5/7) — TFR 2.1',           value: 2.5, tone: 'brass' },
  { label: 'Korean rural Xn — P5 projection (not yet observed)', value: 2.5, tone: 'cognac' },
  { label: 'Kibbutz 1985-95 (2.0/7 decayed) — TFR 2.1',  value: 2.0, tone: 'oxblood' },
  { label: 'France (1.0/7) — TFR 1.62',                  value: 1.0, tone: 'plum' },
  { label: 'Sweden (0.5/7) — TFR 1.65',                  value: 0.5, tone: 'plum' },
  { label: 'Hungary urban (0.5/7) — TFR 1.50',           value: 0.5, tone: 'plum' },
  { label: 'Korea Seoul (0.0/7) — TFR 0.55',             value: 0.0, tone: 'oxblood' },
];

// ─── Predictions ────────────────────────────────────────────────────

export const REFUGIA_PREDICTIONS = [
  {
    label: 'P1 — Hutterite cohort 1985-95 sustains ≥6.0',
    central: 6.0,
    band: [5.8, 6.4],
    falsifierBand: [5.5, 6.6],
    resolutionDate: '2030 cohort closure',
    status: 'open',
    note: 'Hutterite case is the strongest cross-validator: same structural conditions as Haredi, entirely distinct religious tradition (Anabaptist vs Orthodox Jewish). If religion-specific framing were load-bearing, patterns should diverge; framework predicts they do not.',
  },
  {
    label: 'P2 — Amish cohort 1985-95 sustains ≥6.0',
    central: 6.2,
    band: [6.0, 6.5],
    falsifierBand: [5.5, 6.7],
    resolutionDate: '2030 cohort closure (US Census + plain-community studies)',
    status: 'open',
    note: 'Plain-community demographic studies + US Census tracking. Population growth ~3% annually doubles every ~22 years; framework predicts continued sustained-recovery-band fertility.',
  },
  {
    label: 'P3 — Mormon Utah condition decay produces TFR drift toward 2.0',
    central: 2.4,
    band: [2.0, 2.6],
    falsifierBand: [1.8, 2.9],
    resolutionDate: '2035+ US Census + cohort tables (1990-2000 cohort)',
    status: 'open',
    note: 'Tests directionality. As Mormon Utah urbanizes and labor-force-participation aligns with secular Utah, condition-count decays from ~5.5/7 toward ~4/7. Framework predicts cohort fertility follows. Falsified if 1990-2000 cohort sustains ≥2.7 with no condition decay measurable.',
  },
  {
    label: 'P4 — Israel-Arab Muslim cohort 1985-95 lands ~2.5',
    central: 2.5,
    band: [2.3, 2.7],
    falsifierBand: [1.8, 3.2],
    resolutionDate: '2030 ICBS cohort tables',
    status: 'open',
    note: 'Tests transitional-band trajectory. Conditions weakened from 4-5/7 (1960s) to 3/7 (now). Framework predicts continued decline toward Israeli-secular floor as remaining conditions weaken further.',
  },
  {
    label: 'P5 — Korean rural Christian cohort 1985-95 lands ≥1.3',
    central: 1.4,
    band: [1.3, 1.6],
    falsifierBand: [1.0, 1.8],
    resolutionDate: '2030+ KOSIS sub-stratum tables',
    status: 'open',
    note: 'Hypothesis to test, not confirmed case. If Korean rural Christian sub-population produces ~0.7 like Korean Seoul (no condition-driven lift), the framework fails to explain within-Korea variance — wounding result. Documentation is partial; this case is in the hypothesis-to-test category.',
  },
  {
    label: 'P6 — Anti-prediction: no secular kibbutz-revival case reaches cohort TFR ≥4.0 by 2040',
    central: 0,
    band: [0, 0],
    falsifierBand: [0, 1],
    resolutionDate: '2040 cohort completion (1995-2000 cohort)',
    status: 'open',
    note: 'Single-falsifier-suffices design. Tests whether secular structural reproduction can recover the kibbutz lift. State-secular intentional-community / housing-cooperative / structural-reform cases worldwide are in scope. If any reaches cohort TFR ≥4.0, the religious-vs-structural ambiguity resolves toward "structural only".',
  },
  {
    label: 'P7 — Cross-case super-linearity sustains through 2040',
    central: 4.0,
    band: [3.0, 5.0],
    falsifierBand: [1.5, 999],
    resolutionDate: '2040 cumulative cross-case data',
    status: 'open',
    note: 'Slope from 0-2 conditions to 5+ conditions vs slope from 5+ to 7. Framework predicts at least 3× ratio (super-linear). Falsified if relationship becomes linear (ratio ~1.0) or sub-linear in cumulative data through 2040.',
  },
];

// ─── Finding metadata ───────────────────────────────────────────────

export const FINDING_META = {
  slug: 'refugia-template',
  domain: 'popdec',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered',
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/popdec/2026/refugia-template/v1',
  title: 'The Refugia Template: A Cross-Case Test of the Seven-Condition Framework',
  shortTitle: 'Refugia Template',
  pageTitle: 'The Refugia Template — Lab Finding · Shift',
  description:
    'Cross-case validation of the seven-condition structural framework. Six documented sustained-recovery cases (Haredi, Hutterite, Amish, Mormon Utah, Roma, Israel-Arab Muslim transitional) plus one secular failure analog (kibbutz movement). Tests whether the framework generalizes; tests directionality (lift when conditions hold, decay when they weaken). Seven pre-registered predictions resolving 2030-2040.',
  form: 4,
  formName: 'Pattern across cases',
  reflexivity: 'HIGH',
  epistemicStatus: 'mechanism',
  confidence: 'medium',
  sampleSize: 9,
  preregisteredAt: '2026-05-08',
  visibility: 'public-peek',
};

// ─── Article ────────────────────────────────────────────────────────

export default function RefugiaTemplateFinding() {
  return (
    <article className="finding-article">
      <FindingBack href="/findings" label="← Findings index" />

      <FindingHeader
        eyebrow="§ Lab finding · popdec/2026/refugia-template/v1"
        title={FINDING_META.title}
        sub="The fifth popdec finding. Cross-case completion of the structural-positive thesis."
        subItalic="Israel-Haredi documented the seven conditions in one case. This finding tests whether the framework generalizes: six well-documented cases across three continents, two religious traditions, and three documented partial/transitional cases. Plus one secular failure analog (kibbutz movement) that confirms the directionality — lift when the conditions held, collapse when they decayed."
        meta={FINDING_META}
      />

      <section className="finding-lede">
        <p>
          The Israel-Haredi finding made the within-case mechanism claim. Seven structural conditions; cohort fertility ≥6.0 sustained in one OECD-context case for forty years. What it could not test was generalization: <em>does the framework predict outcomes across cases, or is Haredi sui generis?</em> This finding tests the cross-case claim. The Hutterite case is the cleanest cross-validator — entirely distinct religious tradition (Anabaptist), nearly identical structural conditions, nearly identical cohort fertility. The Amish replicate the pattern. Mormon Utah at 5-6/7 conditions sustains in the predicted 2.5-3.0 band. The Israeli kibbutz movement provides the load-bearing failure analog: 4/7 conditions in secular form briefly produced fertility lift; the conditions decayed; the lift collapsed exactly as the framework predicts. Below: the case matrix, the super-linear scatter, the kibbutz trajectory, seven pre-registered predictions, and the falsifier.
        </p>
      </section>

      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          <strong>Pattern documented across cases.</strong> The seven-condition framework predicts cohort fertility within ±0.5 across nine cases spanning religious-Jewish, Anabaptist, LDS, Muslim, Roma, secular-Israeli, and (failed) secular-Israeli-collective contexts. Cases with 6-7 conditions sustain TFR ≥ 6.0; cases with 5/7 sustain ~2.5-3.0; cases with 3-4 are transitional; below 3 cannot reach replacement. The kibbutz failure analog confirms directionality — fertility followed conditions both up and down. <strong>No documented OECD-context case sustains TFR ≥ 4.0 with fewer than 6 conditions present.</strong>
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">cross-case validated</FindingPill>
          <FindingPill tone="warn">Confidence: medium</FindingPill>
          <FindingPill tone="neutral">N=9 cases</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-08</FindingPill>
          <FindingPill tone="failure">Reflexivity: HIGH</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Corpus:</strong> Israel-Haredi (anchor), Hutterite, Amish, Mormon Utah, Israel-Arab Muslim (transitional), Roma settlements, Korean rural Christian (hypothesis), Israel-secular, Kibbutz 1985-95 (failure analog). Sources: CBS Israel + DellaPergola, US Census, Eaton & Mayer 1953, Heaton 1986, Wittgenstein Centre, Human Fertility Database.
        </div>
      </section>

      {/* ── §1 The pattern claim ── */}
      <FindingSection
        eyebrow="§1 The pattern claim"
        title="From single-case to cross-case generalization."
      >
        <p className="finding-prose">
          The Israel-Haredi finding (<code>lab:finding/popdec/2026/israel-haredi-floor/v1</code>) documented seven structural conditions in one case. Briefly:
        </p>
        <ul className="finding-bullets">
          <li><strong>C1</strong> — Religious-community density (peer-parents-visibility ~100%)</li>
          <li><strong>C2</strong> — Multi-generational household economics (housing absorbed by family)</li>
          <li><strong>C3</strong> — Alternative-status structure (status decoupled from income)</li>
          <li><strong>C4</strong> — Kinship-network childcare (cost-compression on later parities)</li>
          <li><strong>C5</strong> — Identity-narrative coherence around family</li>
          <li><strong>C6</strong> — Marriage-formation infrastructure (compressed search cost, early median age)</li>
          <li><strong>C7</strong> — Exit cost (structural stability against drift)</li>
        </ul>
        <p className="finding-prose">
          The within-case finding hypothesized C1, C3, C5 as load-bearing backbone. This finding makes the cross-case claim:
        </p>
        <blockquote className="finding-quote">
          The condition-count predicts the fertility outcome across cases, with a super-linear relationship in count; the conditions can in principle exist in non-religious form but no secular reproduction has sustained at scale.
        </blockquote>
        <p className="finding-prose">
          Six documented cases plus one failure analog test this claim. The framework either generalizes — and predicts each case's cohort fertility within tolerance — or it doesn't, and the Israel-Haredi finding's mechanism claim is properly understood as a single-case dossier with no generalizable structure.
        </p>
      </FindingSection>

      {/* ── §2 The case matrix ── */}
      <FindingSection
        eyebrow="§2 The case matrix"
        title="Nine cases × seven conditions."
      >
        <p className="finding-prose">
          The matrix below documents which conditions are present (sage), partial (brass), or absent (oxblood) in each case. The bottom row documents each case's cohort fertility outcome. The pattern: <strong>condition density predicts fertility outcome; the cases segregate cleanly into sustained-recovery, transitional, and sub-replacement bands.</strong>
        </p>

        <CoOccurrenceMatrix
          title="Nine cases × seven structural conditions [present/partial/absent calls are mechanism-dossier judgments, not measurements]. Sustained-recovery cases (sage outcome) cluster at 5.5-7/7 conditions; transitional cases at 3-4/7; sub-replacement and failed cases at 0-2.5/7."
          cases={REFUGIA_CASES}
          signals={REFUGIA_CONDITIONS}
        />

        <p className="finding-prose">
          <strong>The present/partial/absent calls in the matrix are mechanism-dossier judgments, not coded measurements.</strong> Each cell is a judgment by a single reviewer (the author) based on the demographic and ethnographic literature for each case. The most contestable individual calls (a critic could legitimately push them by ±1 step):
        </p>
        <ul className="finding-bullets">
          <li><strong>Mormon Utah C3 — partial.</strong> Could be 0 (LDS Utah has integrated economically with secular Utah; status increasingly tracks income) or 1 (LDS scholarship + family-size status currency still substantially decoupled within the LDS community). Either is defensible.</li>
          <li><strong>Israel-secular C5 — partial.</strong> Israeli national-mobilization context provides some narrative coherence around family. A critic could argue this is an artifact of Israeli national context, not the Israeli-secular condition state, and code it 0.</li>
          <li><strong>Israel-Arab Muslim C1 — partial.</strong> Varies enormously by community (Negev Bedouin, Galilee, Triangle, mixed cities). Aggregate "partial" coding masks within-population heterogeneity.</li>
          <li><strong>Roma C5 — partial.</strong> Depends heavily on which settlement. Hungarian Roma have stronger family-narrative coherence than Czech or Slovak Roma; the aggregate "partial" coding compresses real variation.</li>
        </ul>
        <p className="finding-prose">
          <strong>The pattern (count → TFR) is robust to small cell-call shifts.</strong> Mormon Utah moving from 5.5/7 → 5/7 doesn't move it out of the sustained-recovery band. Israel-secular at 2/7 vs 3/7 still produces ~2.1 fertility. The sustained-recovery / transitional / sub-replacement bands are wide enough that individual cell judgments don't break the relationship. <em>But</em> if a more rigorous coding scheme (multiple independent coders evaluating each condition for each case, reporting inter-coder agreement) produced systematically different counts that broke the count→TFR relationship, the framework would be wounded. v2 of this finding will integrate inter-coder validation.
        </p>

        <FindingSubhead>The cross-case scatter</FindingSubhead>
        <p className="finding-prose">
          Plotting condition count (x) against cohort fertility (y) reveals the relationship. Note three structural features: the upper cluster (Haredi/Hutterite/Amish at 7/7) sits dramatically above everything else; Mormon Utah at 5.5/7 is the only sustained-recovery case in the mid-band; below 5/7 cohort fertility cannot reach the sustained-recovery zone; the cluster at 0-1/7 collapses to lowest-low fertility (Korea Seoul 0.55).
        </p>

        <CountryScatter
          title="Condition count × cohort TFR — the super-linear pattern. The upper-right region (high condition count, sustained recovery) is dense; the lower-right region (low count, recovery) is empty. The kibbutz outlier (oxblood) sits where the framework predicts: 2/7 decayed conditions, fertility at the secular norm."
          points={REFUGIA_SCATTER}
          xLabel="Condition count (of 7)"
          yLabel="Cohort TFR (1985-95 cohort, completed or projected)"
          xMin={-0.5}
          xMax={7.5}
          yMin={0}
          yMax={7}
        />

        <FindingSubhead>Per-case condition count and TFR</FindingSubhead>
        <p className="finding-prose">
          Sorted by condition count. The bands are clear: sustained-recovery cluster at 5.5-7/7; transitional band at 3-4/7; sub-replacement at 0-2.5/7. Kibbutz at 2/7 (decayed from 4/7 at peak) sits in the failed-secular slot exactly where the framework predicts.
        </p>

        <MagnitudeBars
          title="Per-case condition count next to cohort TFR. Sage = sustained-recovery; brass = transitional; cognac/plum = partial/sub-replacement; oxblood = failed (kibbutz) and lowest-low (Korea Seoul)."
          items={CONDITION_COUNT_BARS}
          valueLabel="condition count"
        />
      </FindingSection>

      {/* ── §3 The mechanism — why super-linear ── */}
      <FindingSection
        eyebrow="§3 The mechanism"
        title="Why super-linear, not linear."
      >
        <p className="finding-prose">
          The conditions are not independent line items. They reinforce each other. Five compounding pairs explain the super-linear shape:
        </p>
        <ul className="finding-bullets">
          <li><strong>C1 (community density) + C5 (identity-narrative coherence).</strong> Density makes large families <em>visible</em>; the narrative makes them <em>desirable</em>. Visibility without narrative is mere observation; narrative without visibility is abstraction. Together they produce decisional clarity.</li>
          <li><strong>C3 (alternative status) + C5 (identity narrative).</strong> Without C3, secular professional achievement competes for status currency; the narrative becomes one option among several. With C3, the community's status currency stays internal and the narrative is unrivaled.</li>
          <li><strong>C2 (multi-gen economics) + C4 (kinship childcare).</strong> Both reduce the marginal cost of additional children, but they compound because the kin-network childcare requires the geographic density that the multi-gen household structure produces. Either alone is partial; together they produce the cost-compression that sustains 6+ child families.</li>
          <li><strong>C6 (marriage infrastructure) + C5 (identity narrative).</strong> Early marriage is enabled by the matchmaking system AND made desirable by the identity narrative around family. Either alone produces partial response; together they push median marriage age to ~21F/~22M, opening 4-7 additional reproductive years.</li>
          <li><strong>C7 (exit cost) stabilizes everything.</strong> Without it, individual exits from the high-fertility expectation accumulate into community decay (the kibbutz trajectory documents this directly).</li>
        </ul>
        <p className="finding-prose">
          This mutual reinforcement produces the super-linear relationship. Removing one condition from a 7/7 stack collapses to 4-5/7-equivalent fertility (per the per-condition counterfactual in the Israel-Haredi finding §4). Adding one condition to a 0-2/7 cluster does <em>not</em> produce a corresponding lift, because the conditions need each other to hold.
        </p>
      </FindingSection>

      {/* ── §4 The failure analog — kibbutz, in detail ── */}
      <FindingSection
        eyebrow="§4 The failure analog"
        title="Kibbutz: secular reproduction, brief lift, full collapse."
      >
        <p className="finding-prose">
          The kibbutz case is the load-bearing inversion. If structural conditions were sufficient regardless of religious framing, the kibbutz movement should have produced sustained recovery. It did not. The conditions decayed; the fertility followed.
        </p>

        <FindingSubhead>Conditions present at peak (1950s-1960s)</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>C1 — Community density: HIGH.</strong> Kibbutz settlements were demographically dense and socially insular.</li>
          <li><strong>C2 — Multi-gen economics: PRESENT in collective form.</strong> Housing, food, healthcare were collectivized.</li>
          <li><strong>C4 — Kinship-network childcare: PRESENT in collective form.</strong> The children's-house system distributed childcare across the collective.</li>
          <li><strong>C5 — Identity-narrative coherence: PARTIAL.</strong> Kibbutz ideology framed large families as Zionist nation-building.</li>
        </ul>

        <FindingSubhead>Conditions absent at peak</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>C3 — Alternative status: ABSENT.</strong> Status was earned through labor and ideology but did not decouple from secular Israeli professional achievement once the kibbutz movement integrated with the national economy.</li>
          <li><strong>C6 — Marriage-formation infrastructure: ABSENT.</strong> No matchmaking system; marriage age progressed toward Israeli national norm.</li>
          <li><strong>C7 — Exit cost: LOW.</strong> Kibbutz members could integrate into Israeli mainstream without identity-loss or kinship-loss.</li>
        </ul>

        <FindingSubhead>The trajectory</FindingSubhead>
        <p className="finding-prose">
          At ~4/7 conditions, kibbutz produced fertility lift. Cohort 1925-45 women raised in kibbutz produced cohort fertility ~3.5-4.0 — well above Israeli secular norm (~2.5-3.0) of the same period. The framework predicts this: 4/7 conditions in an otherwise-supportive national context (high-mobilization Israel) produces transitional-band lift.
        </p>
        <p className="finding-prose">
          Then the conditions decayed. Through 1970s-90s, many kibbutzim privatized (C2 weakened), the children's-house system was dismantled (C4 collapsed), community density weakened as members left or moved to mixed kibbutzim (C1 reduced), and identity-narrative coherence weakened as Zionist nation-building gave way to economic integration (C5 reduced). By the 1985-95 cohort, kibbutz-origin women produce cohort fertility approximately at Israeli secular norm (~2.1).
        </p>
        <p className="finding-prose">
          <strong>The kibbutz cohort fertility values plotted below are approximations</strong> compiled from secondary literature on kibbutz demography (Tzaban, Shamai, others) and Israeli aggregate cohort series, not from a single longitudinal primary cohort study. The shape of the trajectory (lift while conditions held → collapse as they decayed → convergence to Israeli secular norm) is well-documented in the kibbutz-demography literature; the specific values per cohort midpoint are estimates within ±0.3 of plausible ranges. A more rigorous v2 would integrate primary kibbutz cohort-completion data once it is integrated into a single source.
        </p>

        <DivergenceTimeline
          title="Trajectories that confirm the framework's directionality [kibbutz values are approximate]. Haredi flat at 6.5 (anchor). Kibbutz lifted to ~3.9 in the 1940s cohort while ~4/7 conditions held; collapsed to secular norm by the 1985-95 cohort as conditions decayed. Israel-Arab Muslim falling 6.5→3.0 as conditions weakened from ~4-5/7 to 3/7. Mormon Utah declining 4.0→2.7 as conditions decay from ~6/7 toward 5/7."
          series={TRAJECTORY_SERIES}
          xLabel="Birth-cohort midpoint"
          yLabel="Cohort fertility"
          yMin={0}
          yMax={7.5}
        />

        <FindingSubhead>Why this is the load-bearing failure analog</FindingSubhead>
        <p className="finding-prose">
          The kibbutz case is the closest secular reproduction of the structural pattern documented in OECD context. It produced the predicted within-condition-count fertility lift while the conditions held; it produced the predicted decay when conditions weakened. <strong>If the religious framing were load-bearing rather than the structural conditions, kibbutz fertility would have been at the secular floor throughout. It wasn't — and then it was — exactly when the conditions changed.</strong> The framework predicts both directions: lift when conditions hold, collapse when they decay. Kibbutz is not a counter-example to the framework — it is a confirmation in two directions.
        </p>
      </FindingSection>

      {/* ── §5 Pre-registered predictions ── */}
      <FindingSection
        eyebrow="§5 Pre-registered predictions"
        title="Seven predictions. Resolution 2030-2040."
      >
        <p className="finding-prose">
          The cross-case framework makes seven testable predictions. P1-P5 test individual cases against the framework's predicted band. P6 is the anti-prediction: no secular structural-reform case reaches sustained-recovery-band TFR by 2040 (single-falsifier-suffices). P7 tests the super-linear shape directly.
        </p>

        <PredictionsBlock
          title="Pre-registered predictions — cross-case validation, kibbutz inversion test, anti-secular-reproduction"
          predictions={REFUGIA_PREDICTIONS}
        />

        <p className="finding-prose">
          Three of the predictions (P1, P2, P3) are within-existing-case validators — cohort fertility for Hutterite, Amish, and Mormon Utah resolving 2030 or shortly after. P4 tests the transitional trajectory of Israel-Arab Muslim. P5 tests the partial-case hypothesis of Korean rural Christian (the only case with major documentation uncertainty). P6 and P7 are the cross-case structural tests that resolve through 2040 cumulative data.
        </p>
      </FindingSection>

      {/* ── §6 Caveats — first-class content ── */}
      <FindingSection
        eyebrow="§6 Honest accounting"
        title="First-class caveats. Read before citing."
      >
        <FindingSubhead>Sample documentation varies</FindingSubhead>
        <p className="finding-prose">
          Hutterite, Amish, Mormon Utah, and Israel-Haredi are well documented in demographic literature (Eaton &amp; Mayer 1953, Heaton 1986, DellaPergola). Israel-Arab Muslim and Israel-secular are documented in CBS Israel. Roma in Hungary settlements are partially documented. <strong>Korean rural Christian is essentially undocumented at the sub-stratum level</strong> — it appears here as a hypothesis to test against future KOSIS data, not a confirmed cross-case anchor. P5 is registered with explicit acknowledgment that the case is hypothetical pending data.
        </p>

        <FindingSubhead>Religious-vs-secular ambiguity</FindingSubhead>
        <p className="finding-prose">
          The seven conditions cluster with religious-community density empirically. The framework's claim is that the conditions are <em>structural</em> and could in principle exist in non-religious form. The kibbutz case is the historical near-counter-example (briefly ~4/7 in secular form). No documented OECD-context secular case has sustained ≥6/7. P6 is the forward test: if a secular case emerges and produces sustained TFR ≥4.0 by 2040, the religious-vs-structural ambiguity resolves toward "structural only." If P6 holds (no such case emerges), the ambiguity tilts toward "religious framing is itself load-bearing" — the conditions may be necessary but require religious-community structure as a generator.
        </p>

        <FindingSubhead>Within-condition dynamics</FindingSubhead>
        <p className="finding-prose">
          Each condition is operationalized at a coarse grain (present / partial / absent). In practice, conditions vary continuously. Mormon Utah's C3 was strong in the 1960s and has weakened progressively as professional-achievement status competes with religious-scholarship status; this is captured as "5-6/7" rather than "5/7 → 4/7" but the underlying dynamic is continuous. The cross-case framework is a coarse first approximation; finer-grained mechanism dossiers (Form 1) per case are the resolution layer.
        </p>

        <FindingSubhead>Selection effects within sustained-recovery cases</FindingSubhead>
        <p className="finding-prose">
          The sub-population that <em>stays</em> in Haredi/Hutterite/Amish life is selected from those who would naturally have higher fertility intent. Exit rates have been documented at ~10-15% per generation across these communities. The cross-case framework treats the sustained-recovery cohort as a population, not a self-selected core; selection effects are real but bounded. P3 (Mormon Utah declining toward 2.0) implicitly tests selection: as Mormon Utah expands and includes more marginal-commitment members, fertility drifts toward the secular norm, exactly as a selection-effect interpretation would predict.
        </p>

        <FindingSubhead>Israeli national context</FindingSubhead>
        <p className="finding-prose">
          Three of the cross-case anchors (Haredi, Israel-secular, Kibbutz) are Israeli. This is a structural feature: Israel has the demographic granularity (multiple distinct sub-populations with documented separate fertility patterns) that makes within-country cross-case comparison feasible. It is also a confound: Israeli national context (high-mobilization society) elevates baseline fertility across all sub-populations. The framework's prediction (Israel-secular at 2-3/7 produces ~2.1) accounts for this elevated floor; the comparable French or Korean sub-population at 2-3/7 should produce lower fertility (P5 hypothesizes Korean rural Christian at ~1.4, which is ~0.7 below Israel-secular's 2.1).
        </p>

        <FindingSubhead>Failure analog robustness</FindingSubhead>
        <p className="finding-prose">
          The kibbutz case is the only well-documented OECD-context secular-condition-decay case in this finding. A more robust failure-analog corpus would include the historical decline of Catholic Quebec (rapid secularization 1960s-70s), the secularization of Irish family structure (post-1990s), and similar rapid condition-decay cases. These are partial analogs but document similar trajectories — fertility lift while conditions hold, collapse when they decay. v2 of this finding will integrate them.
        </p>

        <FindingSubhead>Reflexivity rating: HIGH</FindingSubhead>
        <p className="finding-prose">
          A widely-cited finding that "religious-community structure is the only documented mechanism for sustained sub-replacement-region recovery" can produce policy mimicry, sectarian backlash, or anti-secular political instrumentalization. <strong>Framed as diagnostic + structural-condition map, not policy advice or normative endorsement.</strong>
        </p>
      </FindingSection>

      {/* ── §7 What this means for the next question ── */}
      <FindingSection
        eyebrow="§7 What this means for the next question"
        title="The corpus arc, completed."
      >
        <p className="finding-prose">
          This finding completes the popdec corpus arc:
        </p>
        <ul className="finding-bullets">
          <li><strong>Static negative</strong> (Korea-Hungary): spending alone fails.</li>
          <li><strong>Dynamic negative</strong> (Korea Lock-In): leverage closes early; structural levers were missed.</li>
          <li><strong>Forward negative</strong> (Positive-Case Search): no OECD policy-driven recovery to 1.70 cohort fertility by 2035.</li>
          <li><strong>Structural positive — single case</strong> (Israel-Haredi Floor): seven structural conditions documented.</li>
          <li><strong>Structural positive — cross-case</strong> (this finding): the framework generalizes; the kibbutz failure analog confirms directionality.</li>
        </ul>

        <p className="finding-prose">
          Reading any single finding weakens the structural argument. Reading all five together completes it.
        </p>

        <FindingSubhead>Open questions for v6+</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>Backbone-vs-supporting test.</strong> The Israel-Haredi finding hypothesized C1, C3, C5 as load-bearing backbone. Cross-case data does not yet test this directly. A targeted Lab simulation (SC-PD-006 candidate) could vary which conditions are present in agent traits and observe whether fertility intent collapses faster when backbone vs supporting conditions are removed.</li>
          <li><strong>Threshold dynamics.</strong> Is the transition from 5/7 to 6/7 a discontinuous jump (Allee-style threshold) or a smooth super-linear curve? Mormon Utah's decline trajectory should inform — if it crosses below 5 conditions cleanly and fertility falls discontinuously, threshold dynamics are confirmed.</li>
          <li><strong>Diaspora vs core dynamics.</strong> Hutterite communities in dispersed locations vs core community zones may show different condition-counts. Diaspora Haredi (NYC, London) vs core Israel-Haredi may differ. Within-case variation is the next resolution layer.</li>
          <li><strong>Substrate-symmetry extension.</strong> The Continuum project's substrate-symmetry thesis (SC-PD-003) extends Allee-threshold dynamics from human to ecological substrates. The seven-condition framework operationalizes the human-substrate version; the eco-substrate analog would be: cooperative-breeding-density × habitat-network-density × mate-finding-cost × kin-recognition. The Form 4 cross-substrate paper is the natural Phase 2 extension.</li>
          <li><strong>Resolution monitoring.</strong> P1-P7 come due 2030-2040. The Track Record updates as each resolves.</li>
        </ul>

        <FindingSubhead>Cross-references</FindingSubhead>
        <p className="finding-prose">
          This finding is the cross-case completion of <code>lab:finding/popdec/2026/israel-haredi-floor/v1</code>, which introduced the seven-condition framework. It is jointly read with <code>lab:finding/popdec/2026/korea-hungary-divergence/v1</code> (static negative), <code>lab:finding/popdec/2026/korea-lockin/v1</code> (dynamic negative), and <code>lab:finding/popdec/2026/positive-case-search/v1</code> (forward negative). All five together form the complete corpus arc.
        </p>
      </FindingSection>

      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>
        <dl className="finding-meta">
          <dt>Methodology Version</dt><dd>Lab V2.0</dd>
          <dt>Form</dt><dd>Form 4 — Pattern across cases</dd>
          <dt>Reflexivity</dt><dd>HIGH — widely-cited findings about sustained-recovery mechanism can produce policy mimicry, sectarian backlash, or anti-secular political instrumentalization</dd>
          <dt>Epistemic status</dt><dd>Mechanism documented + cross-case validated</dd>
          <dt>Sample size</dt><dd>N=9 cases (6 sustained-recovery + 1 secular failure analog + 2 partial / sub-replacement cross-comparisons + 4 sub-replacement reference cluster)</dd>
          <dt>Pre-registration date</dt><dd>2026-05-08. Resolution: 2030 (P1, P2, P4, P5), 2035+ (P3), 2040 (P6, P7)</dd>
          <dt>Visibility</dt><dd>Public · read-only</dd>
          <dt>Citation handle</dt><dd><code>{FINDING_META.citationHandle}</code></dd>
          <dt>Falsifier</dt><dd>Any of: a documented case sustains TFR ≥4.0 with &lt;6 conditions; P1-P5 deviate beyond falsifier band; P6 falsifies (secular case reaches ≥4.0); P7 falsifies (relationship becomes linear or sub-linear); a new sustained-recovery case has condition-count outside predicted band.</dd>
        </dl>
        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). The Refugia Template: A Cross-Case Test of the Seven-Condition Framework Across Sustained Sub-Replacement-Region Recovery Cases. Lab Finding {FINDING_META.citationHandle}.
        </div>
        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-08 — initial pre-registration. Resolution windows: 2030, 2035+, 2040.</p>
        </div>
      </footer>
    </article>
  );
}
